import React, { useState, useEffect } from 'react';
import useDecks from '../hooks/useDecks';
import DeckSelector from './DeckSelector';
import { Modal, Field, fieldClass, btnPrimary, btnGhost } from './ui';
import { hasUIChrome, stripUIChrome } from '../utils/text';

const CardCreateModal = ({ isOpen, onClose, onSave }) => {
  const { decks: availableDecks } = useDecks();

  const [formData, setFormData] = useState({
    name: '',
    textFrench: '',
    translationTigrinya: '',
    pronunciation: '',
    context: '',
    deck: '📚 Vocabulaire Général'
  });
  const [isSaving, setIsSaving] = useState(false);

  // Réinitialiser le formulaire quand le modal s'ouvre
  useEffect(() => {
    if (isOpen && availableDecks.length > 0) {
      setFormData({
        name: '',
        textFrench: '',
        translationTigrinya: '',
        pronunciation: '',
        context: '',
        deck: availableDecks[0] || '📚 Vocabulaire Général'
      });
    }
  }, [isOpen, availableDecks]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.translationTigrinya) {
      alert('Veuillez remplir au minimum le texte anglais et la traduction tigrinya');
      return;
    }

    // Sélection débordante recollée dans le champ : on propose la coupe plutôt
    // que d'envoyer le texte de l'interface dans Notion.
    let translationTigrinya = formData.translationTigrinya;
    if (hasUIChrome(translationTigrinya)) {
      const cleaned = stripUIChrome(translationTigrinya);
      if (!cleaned) {
        alert('La traduction tigrigna ne contient que du texte de l\'application. Ressaisissez-la.');
        return;
      }
      if (!window.confirm(`La traduction tigrigna contient du texte de l'application.\n\nEnregistrer plutôt :\n${cleaned}`)) {
        return;
      }
      translationTigrinya = cleaned;
    }

    setIsSaving(true);

    try {
      const response = await fetch('https://n8n.automationtekleabsam.com/webhook/create-card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          textFrench: formData.textFrench,
          translationTigrinya,
          pronunciation: formData.pronunciation,
          context: formData.context,
          deck: formData.deck
        })
      });

      const result = await response.json();
      console.log('✅ Carte créée:', result);

      if (result.success) {
        // Appeler onSave avec la nouvelle carte
        onSave({
          ...formData,
          translationTigrinya,
          pageId: result.pageId,
          reviewCount: 0,
          successRate: 0,
          level: '🆕 Nouveau',
          interval: 1
        });
        onClose();
      } else {
        alert('Erreur lors de la création de la carte');
      }
    } catch (error) {
      console.error('❌ Erreur création carte:', error);
      alert('Erreur lors de la création de la carte');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Nouvelle carte"
      subtitle="Ajoutée à votre base Notion"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <Field label="Anglais — recto">
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Hello"
            className={fieldClass}
            required
          />
        </Field>

        <Field label="Français — recto (optionnel)">
          <input
            type="text"
            value={formData.textFrench}
            onChange={(e) => handleChange('textFrench', e.target.value)}
            placeholder="Bonjour"
            className={fieldClass}
          />
        </Field>

        {/* Le guèze s'écrit de gauche à droite : pas de dir="rtl" ici. */}
        <Field label="Tigrigna — verso">
          <input
            type="text"
            value={formData.translationTigrinya}
            onChange={(e) => handleChange('translationTigrinya', e.target.value)}
            placeholder="ሰላም"
            className={`${fieldClass} font-geez text-xl`}
            required
          />
        </Field>

        <Field label="Prononciation">
          <input
            type="text"
            value={formData.pronunciation}
            onChange={(e) => handleChange('pronunciation', e.target.value)}
            placeholder="selam"
            className={fieldClass}
          />
        </Field>

        <Field label="Exemple d'utilisation">
          <textarea
            value={formData.context}
            onChange={(e) => handleChange('context', e.target.value)}
            placeholder="ሰላም! ከመይ ኣለኻ? — Bonjour ! Comment vas-tu ?"
            className={`${fieldClass} resize-none`}
            rows="3"
          />
        </Field>

        <DeckSelector
          value={formData.deck}
          onChange={(v) => handleChange('deck', v)}
          decks={availableDecks}
          label="Deck"
          required
        />

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className={`flex-1 ${btnGhost}`}
            disabled={isSaving}
          >
            Annuler
          </button>
          <button type="submit" className={`flex-1 ${btnPrimary}`} disabled={isSaving}>
            {isSaving ? 'Création…' : 'Créer la carte'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CardCreateModal;
